import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useContract } from '../../hooks/useContract';
import toast from 'react-hot-toast';

interface LimitsTabProps {
  inputClasses: string;
  currentWallet: string;
}

interface WithdrawalLimits {
  minimum: number;
  maximum: number;
  daily: number;
  weekly: number;
  monthly: number;
}

export function LimitsTab({ inputClasses, currentWallet }: LimitsTabProps) {
  const { limits, updateWithdrawalLimits } = useContract();
  
  const [withdrawalLimits, setWithdrawalLimits] = useState<WithdrawalLimits>({
    minimum: limits.minimumWithdrawal,
    maximum: limits.maximumWithdrawal,
    daily: limits.maxWithdrawalPerDay,
    weekly: limits.maxWithdrawalPerWeek,
    monthly: limits.maxWithdrawalPerMonth
  });

  // Charger les limites sauvegardées pour ce compte
  useEffect(() => {
    if (currentWallet) {
      const savedLimits = localStorage.getItem(`withdrawalLimits_${currentWallet}`);
      if (savedLimits) {
        setWithdrawalLimits(JSON.parse(savedLimits));
      }
    }
  }, [currentWallet]);

  // Sauvegarder les limites quand elles changent
  useEffect(() => {
    if (currentWallet) {
      localStorage.setItem(`withdrawalLimits_${currentWallet}`, JSON.stringify(withdrawalLimits));
    }
  }, [withdrawalLimits, currentWallet]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateWithdrawalLimits(
        withdrawalLimits.minimum,
        withdrawalLimits.maximum,
        withdrawalLimits.daily,
        withdrawalLimits.weekly,
        withdrawalLimits.monthly
      );
      toast.success('Limites de retrait mises à jour');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la mise à jour des limites');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Withdrawal
          </label>
          <input
            type="number"
            value={withdrawalLimits.minimum}
            onChange={(e) => setWithdrawalLimits(prev => ({
              ...prev,
              minimum: parseInt(e.target.value)
            }))}
            className={inputClasses}
            min="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Maximum Withdrawal
          </label>
          <input
            type="number"
            value={withdrawalLimits.maximum}
            onChange={(e) => setWithdrawalLimits(prev => ({
              ...prev,
              maximum: parseInt(e.target.value)
            }))}
            className={inputClasses}
            min="1"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Daily Limit
          </label>
          <input
            type="number"
            value={withdrawalLimits.daily}
            onChange={(e) => setWithdrawalLimits(prev => ({
              ...prev,
              daily: parseInt(e.target.value)
            }))}
            className={inputClasses}
            min="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weekly Limit
          </label>
          <input
            type="number"
            value={withdrawalLimits.weekly}
            onChange={(e) => setWithdrawalLimits(prev => ({
              ...prev,
              weekly: parseInt(e.target.value)
            }))}
            className={inputClasses}
            min="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Monthly Limit
          </label>
          <input
            type="number"
            value={withdrawalLimits.monthly}
            onChange={(e) => setWithdrawalLimits(prev => ({
              ...prev,
              monthly: parseInt(e.target.value)
            }))}
            className={inputClasses}
            min="1"
          />
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          <Save size={20} />
          Save Limits
        </button>
      </div>
    </form>
  );
}